import { collection, addDoc, getDocs, doc, setDoc, getDoc, deleteDoc, updateDoc, query, where, limit, orderBy, startAfter } from "firebase/firestore";
import { db } from "./config";
import { formatProductForFirestore } from "../Utils/dataHelpers";
import axios from 'axios';

const _cache = {
    products: { ts: 0, data: null, promise: null },
    categories: { ts: 0, data: null, promise: null },
    activeDiscounts: { ts: 0, data: null, promise: null }
};

// Cache for dynamic queries (e.g., category product lists)
const _dynamicCache = new Map();

const _ttlMs = {
    products: 60_000,
    categories: 5 * 60_000,
    activeDiscounts: 60_000
};

async function readThroughCache(key, fetcher, { forceRefresh = false, ttlMs } = {}) {
    const entry = _cache[key];
    if (!entry) return fetcher();

    const ttl = typeof ttlMs === 'number' ? ttlMs : (_ttlMs[key] ?? 0);
    const now = Date.now();

    if (!forceRefresh && entry.data && (now - entry.ts) < ttl) {
        return entry.data;
    }

    if (!forceRefresh && entry.promise) {
        return entry.promise;
    }

    entry.promise = (async () => {
        const data = await fetcher();
        entry.data = data;
        entry.ts = Date.now();
        return data;
    })().finally(() => {
        entry.promise = null;
    });

    return entry.promise;
}

async function readThroughDynamicCache(cacheKey, fetcher, { forceRefresh = false, ttlMs = 60_000 } = {}) {
    const now = Date.now();
    const entry = _dynamicCache.get(cacheKey) || { ts: 0, data: null, promise: null };

    if (!forceRefresh && entry.data && (now - entry.ts) < ttlMs) {
        return entry.data;
    }

    if (!forceRefresh && entry.promise) {
        return entry.promise;
    }

    entry.promise = (async () => {
        const data = await fetcher();
        entry.data = data;
        entry.ts = Date.now();
        return data;
    })().finally(() => {
        entry.promise = null;
    });

    _dynamicCache.set(cacheKey, entry);
    return entry.promise;
}

function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
    }
    return out;
}

function invalidateCache(keys) {
    (keys || []).forEach((key) => {
        if (_cache[key]) {
            _cache[key].ts = 0;
            _cache[key].data = null;
            _cache[key].promise = null;
        }
    });

    // When products/categories change, flush dynamic lists too.
    if ((keys || []).includes('products') || (keys || []).includes('categories')) {
        _dynamicCache.clear();
    }
}

// --- CLOUDINARY CONFIGURATION ---
// 1. Go to your Cloudinary Dashboard to get your Cloud Name.
// 2. Go to Settings > Upload > Upload presets to get your Unsigned Preset Name.
const CLOUDINARY_CLOUD_NAME = "dnyygadwb"; 
const CLOUDINARY_UPLOAD_PRESET = "VetCare"; 

/**
 * Uploads a file to Cloudinary.
 * @param {File} file - The file object from input.
 * @returns {Promise<string>} - The public download URL.
 */
export async function uploadProductImage(file) {
    if (!file) throw new Error("No file selected");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    try {
        const response = await axios.post(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
            formData
        );
        return response.data.secure_url;
    } catch (error) {
        console.error("Cloudinary Upload Error:", error);
        throw new Error("Image upload failed. Check your Cloudinary config.");
    }
}

/**
 * Adds a new product to Firestore with the correct schema.
 * @param {Object} productData - Raw form data
 * @param {Array<File>} imageFiles - The image files to upload (array)
 */
export async function addNewProduct(productData, imageFiles) {
    try {
        // 1. Upload Images
        let imageUrls = [];
        if (imageFiles && imageFiles.length > 0) {
            const uploadPromises = imageFiles.map(file => uploadProductImage(file));
            imageUrls = await Promise.all(uploadPromises);
        }

        // 2. Format Data (Schema Enforcement)
        const formattedData = formatProductForFirestore(productData, imageUrls);

        // 3. Save to Firestore
        const docRef = await addDoc(collection(db, "products"), formattedData);
        invalidateCache(['products']);
        return docRef.id;
    } catch (error) {
        console.error("Error adding product:", error);
        throw error;
    }
}

/**
 * Adds a new brand to Firestore.
 * @param {File} imageFile - The image file to upload
 */
export async function addBrand(imageFile) {
    try {
        const imageUrl = await uploadProductImage(imageFile);
        await addDoc(collection(db, "brands"), { imageUrl });
    } catch (error) {
        console.error("Error adding brand:", error);
        throw error;
    }
}

/**
 * Fetches all brands from Firestore.
 * @returns {Promise<Array>} - List of brands
 */
export async function getBrands() {
    try {
        const querySnapshot = await getDocs(collection(db, "brands"));
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error getting brands:", error);
        return [];
    }
}

/**
 * Uploads a file (image or video) to Cloudinary.
 * @param {File} file - The file object.
 * @returns {Promise<string>} - The public download URL.
 */
export async function uploadFile(file) {
    if (!file) throw new Error("No file selected");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    try {
        // Use 'auto' resource type to handle both images and videos
        const response = await axios.post(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
            formData
        );
        return response.data.secure_url;
    } catch (error) {
        console.error("Cloudinary Upload Error:", error);
        throw new Error("File upload failed. Check your Cloudinary config.");
    }
}

/**
 * Updates the Hero section configuration.
 * @param {Object} heroData - Title, subtitle, button text, etc.
 * @param {File} mediaFile - Optional image or video file.
 */
export async function updateHeroConfig(heroData, mediaFile) {
    try {
        let mediaUrl = heroData.mediaUrl;
        if (mediaFile) {
            mediaUrl = await uploadFile(mediaFile);
        }

        const dataToSave = { 
            ...heroData, 
            mediaUrl: mediaUrl || null,
            updatedAt: new Date().toISOString()
        };
        
        await setDoc(doc(db, "site_content", "hero"), dataToSave);
        return dataToSave;
    } catch (error) {
        console.error("Error updating hero config:", error);
        throw error;
    }
}

/**
 * Fetches the Hero section configuration.
 * @returns {Promise<Object>} - Hero config data
 */
export async function getHeroConfig() {
    try {
        const docRef = doc(db, "site_content", "hero");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error getting hero config:", error);
        return null;
    }
}

/**
 * Updates the Footer configuration.
 * @param {Object} footerData - Address, email, phone, social links.
 */
export async function updateFooterConfig(footerData) {
    try {
        await setDoc(doc(db, 'site_content', 'footer'), footerData);
        return footerData;
    } catch (error) {
        console.error('Error updating footer config:', error);
        throw error;
    }
}

/**
 * Fetches the Footer configuration.
 * @returns {Promise<Object>} - Footer config data
 */
export async function getFooterConfig() {
    try {
        const docRef = doc(db, 'site_content', 'footer');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting footer config:', error);
        return null;
    }
}

/**
 * Updates the Header configuration.
 * @param {File} logoFile - Optional logo file.
 */
export async function updateHeaderConfig(logoFile) {
    try {
        let logoUrl = null;
        if (logoFile) {
            logoUrl = await uploadFile(logoFile);
        }

        const dataToSave = { 
            updatedAt: new Date().toISOString()
        };
        if (logoUrl) dataToSave.logoUrl = logoUrl;
        
        await setDoc(doc(db, 'site_content', 'header'), dataToSave, { merge: true });
        return dataToSave;
    } catch (error) {
        console.error('Error updating header config:', error);
        throw error;
    }
}

/**
 * Fetches the Header configuration.
 * @returns {Promise<Object>} - Header config data
 */
export async function getHeaderConfig() {
    try {
        const docRef = doc(db, 'site_content', 'header');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting header config:', error);
        return null;
    }
}


/**
 * Deletes a brand from Firestore.
 * @param {string} brandId - The ID of the brand to delete.
 */
export async function deleteBrand(brandId) {
    try {
        await deleteDoc(doc(db, 'brands', brandId));
    } catch (error) {
        console.error('Error deleting brand:', error);
        throw error;
    }
}

/**
 * Updates a specific info page content.
 * @param {string} pageId - The ID of the page (e.g., 'about', 'privacy').
 * @param {string} content - The text content of the page.
 */
export async function updatePageContent(pageId, content) {
    try {
        const docRef = doc(db, "site_content", "pages");
        await setDoc(docRef, { [pageId]: content }, { merge: true });
        return content;
    } catch (error) {
        console.error(`Error updating page ${pageId}:`, error);
        throw error;
    }
}

/**
 * Fetches all info pages content.
 * @returns {Promise<Object>} - Object containing content for all pages.
 */
export async function getPagesContent() {
    try {
        const docRef = doc(db, "site_content", "pages");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return {};
        }
    } catch (error) {
        console.error("Error getting pages content:", error);
        return {};
    }
}

/**
 * Fetches all products from Firestore.
 * @returns {Promise<Array>} - List of products
 */
export async function getProducts() {
    return readThroughCache('products', async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "products"));
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error getting products:", error);
            return [];
        }
    });
}

/**
 * Searches products server-side using the `searchKeywords` array.
 * This avoids downloading all products just to search.
 */
export async function searchProducts({ text, maxResults = 10 } = {}) {
    try {
        const normalized = (text || '').toString().trim().toLowerCase();
        if (!normalized) return [];

        // Firestore array-contains works on single tokens. Use the first token as the driver.
        const token = normalized
            .split(/\s+/)
            .map(t => t.replace(/[^\p{L}\p{N}_-]/gu, ''))
            .find(Boolean);

        if (!token) return [];

        // Pull a little more than needed so the caller can filter client-side (e.g., by category).
        const fetchLimit = Math.min(Math.max(maxResults * 4, 10), 40);
        const q = query(
            collection(db, 'products'),
            where('searchKeywords', 'array-contains', token),
            limit(fetchLimit)
        );

        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('Error searching products:', error);
        return [];
    }
}

/**
 * Fetches products in pages to avoid loading the whole collection.
 * Uses `orderBy('__name__')` to avoid relying on schema fields.
 * @returns {Promise<{items: Array, nextCursor: any}>}
 */
export async function getProductsPage({ pageSize = 10, cursor = null } = {}) {
    try {
        const base = [
            collection(db, 'products'),
            orderBy('__name__'),
            limit(pageSize)
        ];

        const q = cursor
            ? query(...base, startAfter(cursor))
            : query(...base);

        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        return { items, nextCursor };
    } catch (error) {
        console.error('Error getting products page:', error);
        return { items: [], nextCursor: null };
    }
}

/**
 * Fetches products that belong to a category ID (paged via limit).
 * Useful when products store category IDs and you want to search by category name.
 */
export async function searchProductsByCategoryId({ categoryId, maxResults = 20 } = {}) {
    try {
        if (!categoryId) return [];
        const q = query(
            collection(db, 'products'),
            where('categories', 'array-contains', categoryId),
            limit(Math.min(Math.max(maxResults, 1), 40))
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('Error searching products by categoryId:', error);
        return [];
    }
}

/**
 * Fetches a small list of products that match a single category term.
 * Works for both modern schema (`categories` array containing IDs) and legacy schema
 * (either `categories` array containing names, or `category` string).
 */
export async function searchProductsByCategoryTerm({ term, maxResults = 4 } = {}) {
    try {
        const normalized = term != null ? String(term).trim() : '';
        if (!normalized) return [];

        const lim = Math.min(Math.max(maxResults, 1), 40);

        const [byArray, byLegacy] = await Promise.all([
            getDocs(query(
                collection(db, 'products'),
                where('categories', 'array-contains', normalized),
                limit(lim)
            )),
            getDocs(query(
                collection(db, 'products'),
                where('category', '==', normalized),
                limit(lim)
            ))
        ]);

        const merged = new Map();
        [...byArray.docs, ...byLegacy.docs].forEach((d) => {
            if (!merged.has(d.id)) merged.set(d.id, { id: d.id, ...d.data() });
        });

        return Array.from(merged.values()).slice(0, lim);
    } catch (error) {
        console.error('Error searching products by category term:', error);
        return [];
    }
}

/**
 * Fetches ALL products that belong to any of the provided category IDs/names.
 * This is intended for Shop category pages so we don't download the whole collection.
 *
 * Notes:
 * - Firestore `array-contains-any` supports up to 10 values per query, so we chunk.
 * - Also optionally queries legacy `category` string field using `in` (also max 10).
 */
export async function getProductsForCategories({ categoryIds = [], categoryNames = [] } = {}) {
    try {
        const ids = (categoryIds || []).map(String).filter(Boolean);
        const names = (categoryNames || []).map(String).filter(Boolean);
        const terms = Array.from(new Set([...ids, ...names]));
        const legacyNames = Array.from(new Set(names));

        if (terms.length === 0 && legacyNames.length === 0) return [];

        const cacheKey = `productsByCategories:${terms.slice().sort().join('|')}::legacy:${legacyNames.slice().sort().join('|')}`;
        return await readThroughDynamicCache(cacheKey, async () => {
            const byIdOrArrayNameChunks = chunkArray(terms, 10);
            const legacyNameChunks = chunkArray(legacyNames, 10);

            const resultsById = await Promise.all(
                byIdOrArrayNameChunks.map(async (chunk) => {
                    const q = query(
                        collection(db, 'products'),
                        where('categories', 'array-contains-any', chunk)
                    );
                    const snap = await getDocs(q);
                    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
                })
            );

            const resultsByLegacy = await Promise.all(
                legacyNameChunks.map(async (chunk) => {
                    // Legacy exact match field
                    const q = query(
                        collection(db, 'products'),
                        where('category', 'in', chunk)
                    );
                    const snap = await getDocs(q);
                    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
                })
            );

            const merged = new Map();
            [...resultsById.flat(), ...resultsByLegacy.flat()].forEach((p) => {
                if (p && p.id && !merged.has(p.id)) merged.set(p.id, p);
            });

            return Array.from(merged.values());
        }, { ttlMs: 60_000 });
    } catch (error) {
        console.error('Error getting products for categories:', error);
        return [];
    }
}

/**
 * Fetches products for a set of category terms (IDs and/or names) with cursor-based pagination.
 *
 * This is designed for the Shop leaf-category page to reduce read cost:
 * - Uses `array-contains-any` on `categories` and pages using `orderBy('__name__')` + `startAfter`.
 * - Firestore limits `array-contains-any` to 10 terms, so `terms` must be <= 10.
 *
 * @returns {Promise<{items: Array, nextCursor: any}>}
 */
export async function getProductsForCategoryTermsPage({ terms = [], pageSize = 12, cursor = null } = {}) {
    try {
        const normalizedTerms = Array.from(
            new Set((terms || []).map(t => t != null ? String(t) : '').map(s => s.trim()).filter(Boolean))
        );

        if (normalizedTerms.length === 0) return { items: [], nextCursor: null };
        if (normalizedTerms.length > 10) {
            console.warn('getProductsForCategoryTermsPage: terms > 10; caller should chunk or fallback.');
            return { items: [], nextCursor: null };
        }

        const base = [
            collection(db, 'products'),
            where('categories', 'array-contains-any', normalizedTerms),
            orderBy('__name__'),
            limit(Math.min(Math.max(pageSize, 1), 40))
        ];

        const q = cursor
            ? query(...base, startAfter(cursor))
            : query(...base);

        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        return { items, nextCursor };
    } catch (error) {
        console.error('Error getting category terms page:', error);
        return { items: [], nextCursor: null };
    }
}

/**
 * Updates an existing product.
 * @param {string} productId - The ID of the product.
 * @param {Object} productData - The new data.
 * @param {File} imageFile - Optional new image file.
 */
export async function updateProduct(productId, productData, imageFiles) {
    try {
        let imageUrls = productData.imageUrls || [];
        
        // Handle new image uploads
        if (Array.isArray(imageFiles) && imageFiles.length > 0) {
             const uploadPromises = imageFiles.map(file => uploadProductImage(file));
             const newUrls = await Promise.all(uploadPromises);
             imageUrls = [...imageUrls, ...newUrls];
        } else if (imageFiles && !Array.isArray(imageFiles)) {
             // Backward compatibility for single file
             const url = await uploadProductImage(imageFiles);
             imageUrls = [...imageUrls, url];
        }

        // Ensure we don't lose existing images if they were passed in productData
        // (The UI should pass existing URLs in productData.imageUrls)

        const formattedData = formatProductForFirestore({ ...productData, imageUrls });
        
        const docRef = doc(db, "products", productId);
        await updateDoc(docRef, formattedData);
        invalidateCache(['products']);
    } catch (error) {
        console.error("Error updating product:", error);
        throw error;
    }
}

/**
 * Deletes a product from Firestore.
 * @param {string} productId - The ID of the product.
 */
export async function deleteProduct(productId) {
    try {
        await deleteDoc(doc(db, "products", productId));
        invalidateCache(['products']);
    } catch (error) {
        console.error("Error deleting product:", error);
        throw error;
    }
}

/**
 * Fetches the user's role.
 * @param {string} uid - The user's UID.
 * @returns {Promise<string>} - 'admin' or 'moderator'
 */
export async function getUserRole(uid) {
    try {
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const role = docSnap.data()?.role;
            return typeof role === 'string' ? role.toLowerCase() : null;
        } else {
            // No user doc -> treat as non-staff (prevents accidental admin access)
            return null;
        }
    } catch (error) {
        console.error("Error getting user role:", error);
        return null;
    }
}

/**
 * Fetches the user's full profile data.
 * @param {string} uid - The user's UID.
 * @returns {Promise<Object|null>} - User data object or null
 */
export async function getUserProfile(uid) {
    try {
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error getting user profile:", error);
        return null;
    }
}

// --- ROLE / PERMISSIONS SERVICES ---

/**
 * Fetches permissions for a role from Firestore.
 * Schema: roles/{roleId} -> { permissions: { [permissionKey]: boolean } }
 */
export async function getRolePermissions(roleId) {
    if (!roleId) return null;
    try {
        const roleRef = doc(db, 'roles', roleId);
        const roleSnap = await getDoc(roleRef);
        if (!roleSnap.exists()) return null;
        return roleSnap.data()?.permissions || null;
    } catch (error) {
        console.error('Error getting role permissions:', error);
        return null;
    }
}

/**
 * Upserts a role permissions doc.
 */
export async function setRolePermissions(roleId, permissionsMap) {
    if (!roleId) throw new Error('roleId is required');
    try {
        const roleRef = doc(db, 'roles', roleId);
        await setDoc(roleRef, {
            permissions: permissionsMap || {},
            updatedAt: new Date().toISOString()
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('Error setting role permissions:', error);
        throw error;
    }
}

/**
 * Creates the role doc if it does not exist.
 */
export async function ensureRolePermissionsDoc(roleId, defaultPermissionsMap) {
    if (!roleId) return false;
    try {
        const roleRef = doc(db, 'roles', roleId);
        const roleSnap = await getDoc(roleRef);
        const defaults = defaultPermissionsMap || {};

        // If the doc exists, make sure any NEW default permissions are added without
        // overwriting existing choices. This is important because Firestore rules
        // check role permissions server-side.
        if (roleSnap.exists()) {
            const current = roleSnap.data()?.permissions || {};
            const toAdd = {};
            Object.keys(defaults).forEach((k) => {
                if (!(k in current)) toAdd[k] = !!defaults[k];
            });

            if (Object.keys(toAdd).length > 0) {
                await setDoc(roleRef, {
                    permissions: toAdd,
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            }
            return false;
        }

        await setDoc(roleRef, {
            permissions: defaults,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('Error ensuring role permissions doc:', error);
        return false;
    }
}

/**
 * Updates a user's role in Firestore.
 */
export async function updateUserRole(userId, newRole) {
    if (!userId) throw new Error('userId is required');
    if (!newRole) throw new Error('newRole is required');
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            role: newRole,
            updatedAt: new Date().toISOString()
        });
        return true;
    } catch (error) {
        console.error('Error updating user role:', error);
        throw error;
    }
}

/**
 * Optional per-user override permissions (merged over role permissions).
 */
export async function updateUserPermissionsOverride(userId, permissionsMap) {
    if (!userId) throw new Error('userId is required');
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            permissionsOverride: permissionsMap || {},
            updatedAt: new Date().toISOString()
        });
        return true;
    } catch (error) {
        console.error('Error updating user permissions override:', error);
        throw error;
    }
}

// --- CATEGORY SERVICES ---

export async function addCategory(categoryData, imageFile) {
    try {
        let imageUrl = '';
        if (imageFile) {
            imageUrl = await uploadProductImage(imageFile);
        }

        const data = {
            ...categoryData,
            imageUrl,
            createdAt: new Date()
        };

        const docRef = await addDoc(collection(db, "categories"), data);
        invalidateCache(['categories']);
        return docRef.id;
    } catch (error) {
        console.error("Error adding category:", error);
        throw error;
    }
}

export async function getCategories() {
    return readThroughCache('categories', async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "categories"));
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching categories:", error);
            return [];
        }
    });
}

export async function updateCategory(categoryId, categoryData, imageFile) {
    try {
        let imageUrl = categoryData.imageUrl;
        if (imageFile) {
            imageUrl = await uploadProductImage(imageFile);
        }

        const data = {
            ...categoryData,
            imageUrl,
            updatedAt: new Date()
        };

        const docRef = doc(db, "categories", categoryId);
        await updateDoc(docRef, data);
        invalidateCache(['categories']);
    } catch (error) {
        console.error("Error updating category:", error);
        throw error;
    }
}

export async function deleteCategory(categoryId) {
    try {
        await deleteDoc(doc(db, "categories", categoryId));
        invalidateCache(['categories']);
    } catch (error) {
        console.error("Error deleting category:", error);
        throw error;
    }
}

/**
 * Fetches active discounts (cached) to reduce Firestore reads.
 * This is intentionally cached because discounts are queried many times (e.g. product cards).
 */
export async function getActiveDiscounts() {
    return readThroughCache('activeDiscounts', async () => {
        try {
            const q = query(collection(db, 'discounts'), where('isActive', '==', true));
            const discountsSnap = await getDocs(q);
            const discountsData = discountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Extra safety: filter expired discounts client-side (expiryDate schema may vary).
            const now = new Date();
            return discountsData.filter(discount => {
                if (!discount.isActive) return false;
                if (!discount.expiryDate) return true;

                // Supports ISO string or Firestore Timestamp-like objects
                const expiry = typeof discount.expiryDate === 'string'
                    ? new Date(discount.expiryDate)
                    : (discount.expiryDate?.toDate ? discount.expiryDate.toDate() : new Date(discount.expiryDate));

                return expiry > now;
            });
        } catch (error) {
            console.error('Error fetching active discounts:', error);
            return [];
        }
    });
}

// --- HOME PAGE CONTENT SERVICES ---

/**
 * Updates the Offers section configuration.
 * @param {Array} offersData - Array of offer objects.
 */
export async function updateOffersConfig(offersData) {
    try {
        // Upload images if they are File objects
        const processedOffers = await Promise.all(offersData.map(async (offer) => {
            let imageUrl = offer.imageUrl;
            if (offer.imageFile) {
                imageUrl = await uploadFile(offer.imageFile);
            }
            // Remove the file object before saving to Firestore
            const { imageFile, ...rest } = offer;
            return { ...rest, imageUrl };
        }));

        await setDoc(doc(db, 'site_content', 'offers'), { offers: processedOffers });
        return processedOffers;
    } catch (error) {
        console.error('Error updating offers config:', error);
        throw error;
    }
}

/**
 * Fetches the Offers section configuration.
 * @returns {Promise<Array>} - Array of offer objects.
 */
export async function getOffersConfig() {
    try {
        const docRef = doc(db, 'site_content', 'offers');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data().offers || [];
        } else {
            return [];
        }
    } catch (error) {
        console.error('Error getting offers config:', error);
        return [];
    }
}

/**
 * Updates the Services section configuration.
 * @param {Array} servicesData - Array of service objects.
 */
export async function updateServicesConfig(servicesData) {
    try {
        // Upload icons if they are File objects
        const processedServices = await Promise.all(servicesData.map(async (service) => {
            let iconUrl = service.iconUrl;
            if (service.iconFile) {
                iconUrl = await uploadFile(service.iconFile);
            }
            const { iconFile, ...rest } = service;
            return { ...rest, iconUrl };
        }));

        await setDoc(doc(db, 'site_content', 'services'), { services: processedServices });
        return processedServices;
    } catch (error) {
        console.error('Error updating services config:', error);
        throw error;
    }
}

/**
 * Fetches the Services section configuration.
 * @returns {Promise<Array>} - Array of service objects.
 */
export async function getServicesConfig() {
    try {
        const docRef = doc(db, 'site_content', 'services');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data().services || [];
        } else {
            return [];
        }
    } catch (error) {
        console.error('Error getting services config:', error);
        return [];
    }
}

/**
 * Updates the CTA section configuration.
 * @param {Object} ctaData - CTA data object.
 * @param {File} bannerFile - Optional banner image file.
 * @param {File} iconFile - Optional icon image file.
 */
export async function updateCtaConfig(ctaData, bannerFile, iconFile) {
    try {
        let bannerUrl = ctaData.bannerUrl;
        let iconUrl = ctaData.iconUrl;

        if (bannerFile) {
            bannerUrl = await uploadFile(bannerFile);
        }
        if (iconFile) {
            iconUrl = await uploadFile(iconFile);
        }

        const dataToSave = {
            ...ctaData,
            bannerUrl: bannerUrl || null,
            iconUrl: iconUrl || null,
            updatedAt: new Date().toISOString()
        };

        await setDoc(doc(db, 'site_content', 'cta'), dataToSave);
        return dataToSave;
    } catch (error) {
        console.error('Error updating CTA config:', error);
        throw error;
    }
}

/**
 * Fetches the CTA section configuration.
 * @returns {Promise<Object>} - CTA config data.
 */
export async function getCtaConfig() {
    try {
        const docRef = doc(db, 'site_content', 'cta');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting CTA config:', error);
        return null;
    }
}

